/**
 * VoiceRecorder — hold-to-record voice button.
 * Uses MediaRecorder API (browser native).
 *
 * Props:
 *  - onCapture: (blob, durationSeconds) => Promise<void>
 *  - disabled: boolean
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function VoiceRecorder({ onCapture, disabled = false }) {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  async function startRecording() {
    if (disabled || isRecording) return;
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const duration = (Date.now() - startTimeRef.current) / 1000;

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setElapsed(0);

        try {
          await onCapture(blob, duration);
        } catch (err) {
          setError(err.message || 'Upload failed');
        }
      };

      recorder.start();
      startTimeRef.current = Date.now();
      setIsRecording(true);

      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 100);
    } catch (err) {
      setError(err.message || t('brainDump.micDenied', 'Mikrofon-Zugriff verweigert'));
    }
  }

  function stopRecording() {
    if (!isRecording || !mediaRecorderRef.current) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    mediaRecorderRef.current.stop();
    setIsRecording(false);
  }

  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onMouseDown={startRecording}
        onMouseUp={stopRecording}
        onMouseLeave={isRecording ? stopRecording : undefined}
        onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
        onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
        disabled={disabled}
        className={`
          w-28 h-28 md:w-32 md:h-32 rounded-full font-medium text-white shadow-lg transition-all
          flex items-center justify-center text-5xl
          ${isRecording
            ? 'bg-[var(--aif-color-danger)] animate-pulse scale-110'
            : 'bg-[var(--aif-color-primary)] hover:opacity-90 active:scale-95'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        aria-label={isRecording
          ? t('brainDump.stopRecording', 'Aufnahme stoppen')
          : t('brainDump.startRecording', 'Aufnahme starten')}
        data-testid="voice-recorder-btn"
      >
        🎤
      </button>
      <div className="mt-2 text-sm text-[var(--aif-text-muted)] text-center">
        {isRecording ? (
          <span className="text-[var(--aif-color-danger)] font-medium">
            {t('brainDump.recording', 'Aufnahme...')} {elapsed}s
          </span>
        ) : (
          <span>{t('brainDump.holdToRecord', 'Halten zum Aufnehmen')}</span>
        )}
      </div>
      {error && (
        <div className="mt-2 text-xs text-[var(--aif-color-danger)] bg-[var(--aif-color-danger-light)] px-2 py-1 rounded">
          {error}
        </div>
      )}
    </div>
  );
}
